/* ============================================================
   SCENTIMENTAL — HOMEPAGE REDESIGN behaviour
   ------------------------------------------------------------
   Scoped entirely to the `sh-` sections. Loaded only on the
   homepage, after custom.js. Nothing here binds to a cart form:
   add-to-cart on .scentimental-add-to-cart-form is handled by the
   theme's own cart.js (add -> fetchCart -> renderCartDrawer ->
   openCartDrawer). Binding a second listener here would add every
   line to the cart twice — that was the 24 Aug stale-drawer bug.
   ============================================================ */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- hero carousel ---------- */

  function initHero(root) {
    // shopify:section:load re-runs init() across the whole page, not just the
    // section that reloaded, so every editor save used to bind another set of
    // handlers to a hero that already had them. Harmless-looking until the
    // swipe below, which listens on `document` for mouseup — those stack and
    // never get collected. One init per element, and a genuinely re-rendered
    // section arrives as a fresh element without the flag.
    if (root.getAttribute('data-sh-hero-ready') === '1') return;
    root.setAttribute('data-sh-hero-ready', '1');

    var slides = Array.prototype.slice.call(root.querySelectorAll('.sh-hero__slide'));

    var dots = Array.prototype.slice.call(root.querySelectorAll('.sh-hero__dot'));
    var current = root.querySelector('[data-sh-hero-current]');
    var index = 0;
    var timer = null;

    /* ---- slide videos ----
       The markup ships these with preload="none" and no autoplay, so nothing
       is fetched until we call play(). That is the whole point: a 4-slide hero
       left on autoplay would pull down four videos at once on first paint.
       We play the active slide only, and never on a phone when the block has
       opted out — with preload="none" that opt-out costs the visitor 0 bytes. */
    var mqMobile = window.matchMedia('(max-width: 749px)');

    var videos = slides.map(function (slide) {
      var wrap = slide.querySelector('[data-hero-video]');
      var el = wrap && wrap.querySelector('video');
      if (!el) return null;
      return { wrap: wrap, el: el, noMobile: wrap.hasAttribute('data-no-mobile') };
    });

    var hasVideo = videos.some(Boolean);

    function playable(entry) {
      if (!entry || reduceMotion) return false;
      return !(entry.noMobile && mqMobile.matches);
    }

    function syncVideos() {
      videos.forEach(function (entry, i) {
        if (!entry) return;
        if (i === index && playable(entry)) {
          // play() rejects if the browser blocks it; swallow so one blocked
          // slide cannot break the carousel's own rotation.
          var p = entry.el.play();
          if (p && p.catch) p.catch(function () {});
          entry.wrap.classList.add('is-playing');
        } else {
          entry.el.pause();
          entry.wrap.classList.remove('is-playing');
        }
      });
    }

    if (hasVideo) {
      syncVideos();
      // Rotating a phone can cross the 749px boundary and flip `noMobile`.
      if (mqMobile.addEventListener) {
        mqMobile.addEventListener('change', syncVideos);
      } else if (mqMobile.addListener) {
        mqMobile.addListener(syncVideos);
      }
    }

    // A one-slide hero has no carousel to run, but its video still needed
    // starting above — so this early return comes after the video setup.
    if (slides.length < 2) return;

    var autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
    var speed = (parseInt(root.dataset.speed, 10) || 7) * 1000;

    function go(next) {
      index = (next + slides.length) % slides.length;

      slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === index);
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      if (current) {
        // padStart, not '0' + n — at 10+ slides the naive form renders "010".
        current.textContent = String(index + 1).padStart(2, '0');
      }
      if (hasVideo) syncVideos();
    }

    function start() {
      if (!autoplay) return;
      stop();
      timer = setInterval(function () { go(index + 1); }, speed);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        go(parseInt(d.dataset.goto, 10) || 0);
        start();
      });
    });

    /* ---- swipe / drag ----
       The slides crossfade in place rather than riding a track, so there is
       nothing to drag in real time — we read the whole gesture on release and
       step one slide. Two guards matter:
         * the horizontal component has to beat the vertical one, or a thumb
           scrolling the page past a full-bleed hero steals a slide on every
           flick;
         * a drag starting on the CTA or a dot is ignored outright, so a click
           stays a click.
       Listeners are passive: we never call preventDefault, because vertical
       scrolling through the hero must keep working normally. */
    var SWIPE_MIN = 45;   // px of travel before it counts as a swipe
    var SWIPE_BIAS = 1.2; // how much more horizontal than vertical it must be
    var sx = 0, sy = 0, tracking = false;

    function swipeStart(x, y) {
      sx = x;
      sy = y;
      tracking = true;
      stop(); // don't let autoplay fire mid-gesture
    }

    function swipeEnd(x, y) {
      if (!tracking) return;
      tracking = false;

      var dx = x - sx;
      var dy = y - sy;

      if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * SWIPE_BIAS) {
        go(dx < 0 ? index + 1 : index - 1);
      }
      start();
    }

    root.addEventListener('touchstart', function (e) {
      // Ignore pinch/multi-touch — that is a zoom, not a swipe.
      if (!e.touches || e.touches.length !== 1) return;
      swipeStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });

    root.addEventListener('touchend', function (e) {
      var t = e.changedTouches && e.changedTouches[0];
      if (t) swipeEnd(t.clientX, t.clientY);
    }, { passive: true });

    root.addEventListener('touchcancel', function () {
      tracking = false;
      start();
    }, { passive: true });

    // Mouse drag, so the hero behaves the same way on a trackpad.
    root.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      if (e.target.closest && e.target.closest('a, button')) return;
      swipeStart(e.clientX, e.clientY);
    });

    // On document, not root: a drag that ends outside the hero still needs
    // resolving, otherwise `tracking` sticks and autoplay never restarts.
    document.addEventListener('mouseup', function (e) {
      if (tracking) swipeEnd(e.clientX, e.clientY);
    });

    // Arrow keys once focus is inside the hero — the dots are real buttons,
    // so tab-then-arrow is the keyboard equivalent of a swipe. Scoped to the
    // hero rather than document so it cannot hijack the page's arrow keys.
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { go(index - 1); start(); }
      else if (e.key === 'ArrowRight') { go(index + 1); start(); }
    });

    // Pause while the pointer is over the hero, and whenever the tab is
    // backgrounded — otherwise the slide index races ahead unseen.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { start(); }
    });

    start();
  }

  /* ---------- bestseller tabs ---------- */

  function initTabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.sh-tab'));
    if (!tabs.length) return;

    var panels = Array.prototype.slice.call(root.querySelectorAll('.sh-panel'));

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var id = tab.dataset.panel;

        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.id === id);
        });
      });
    });
  }

  /* ---------- reels ----------
     Two problems this solves. (1) The cards are muted and always will be:
     autoplay policy refuses sound without a gesture, so a card that asked for
     it would just be blocked and freeze. Sound lives in a popup player that
     opens from a real click, which is allowed to unmute and shows the native
     controls (scrub / volume / fullscreen). (2) Six 9:16 clips decoding at
     once saturates a mid-range phone, which is why the band stutters — so
     only the cards actually on screen are left running. */

  var IG_SVG = '<svg class="sh-ig" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true" focusable="false"><rect x="3" y="3" width="18" height="18" rx="5.2"/><circle cx="12" cy="12" r="4"/><circle cx="17.3" cy="6.7" r="1.15" fill="currentColor" stroke="none"/></svg>';
  var X_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  function chevronSvg(dir) {
    var d = dir === 'prev' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7';
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="' + d + '"/></svg>';
  }

  // Every card video on the page, with its current on-screen state. The popup
  // pauses the lot while it is open and hands playback back on close.
  var reelVideos = [];

  function reelsPauseAll() {
    reelVideos.forEach(function (rec) { rec.el.pause(); });
  }

  function reelsResumeVisible() {
    if (reduceMotion) return;
    reelVideos.forEach(function (rec) {
      if (!rec.visible) return;
      var p = rec.el.play();
      if (p && p.catch) p.catch(function () {});
    });
  }

  var reelPlayer = (function () {
    var el = null;
    var group = [];
    var index = 0;
    var lastFocus = null;

    function isOpen() { return !!el && !el.hidden; }

    function frameEl() { return el.querySelector('.sh-lb__frame'); }

    function teardown() {
      var frame = frameEl();
      var v = frame.querySelector('video');
      if (v) {
        v.pause();
        // Strip the sources and re-load, otherwise Chrome keeps pulling the
        // clip down in the background long after the popup is closed.
        v.removeAttribute('src');
        while (v.firstChild) v.removeChild(v.firstChild);
        v.load();
      }
      frame.innerHTML = '';
    }

    function render() {
      var card = group[index];
      if (!card) return;

      teardown();

      var source = card.querySelector('video');
      var v = document.createElement('video');
      v.className = 'sh-lb__player';
      v.controls = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.preload = 'auto';

      var poster = source && source.getAttribute('poster');
      if (poster) v.setAttribute('poster', poster);

      // Clone the card's own sources rather than threading the URL through a
      // data attribute — video_tag may emit both an HLS and an MP4 source and
      // the browser should keep picking between them exactly as it did.
      if (source) {
        var sources = source.querySelectorAll('source');
        if (sources.length) {
          Array.prototype.forEach.call(sources, function (s) {
            var c = document.createElement('source');
            c.src = s.src; // .src, not getAttribute — resolves the URL once, here
            if (s.type) c.type = s.type;
            v.appendChild(c);
          });
        } else if (source.currentSrc || source.src) {
          v.src = source.currentSrc || source.src;
        }
      }

      frameEl().appendChild(v);

      // The entire point of the popup. We are inside the click handler's
      // gesture, so the browser permits sound here.
      v.muted = false;
      var p = v.play();
      if (p && p.catch) {
        p.catch(function () {
          // Older iOS and hardened autoplay settings still say no. Falling
          // back to muted beats leaving a black rectangle — the visitor can
          // unmute from the controls that are already on screen.
          v.muted = true;
          var q = v.play();
          if (q && q.catch) q.catch(function () {});
        });
      }

      el.querySelector('.sh-lb__caption').textContent = card.getAttribute('data-reel-caption') || '';

      var insta = card.getAttribute('data-reel-insta') || '';
      var instaEl = el.querySelector('.sh-lb__insta');
      instaEl.hidden = !insta;
      if (insta) instaEl.href = insta;

      var shop = card.getAttribute('data-reel-shop') || '';
      var shopEl = el.querySelector('.sh-lb__shop');
      shopEl.hidden = !shop;
      if (shop) shopEl.href = shop;

      var many = group.length > 1;
      el.querySelector('.sh-lb__nav--prev').hidden = !many;
      el.querySelector('.sh-lb__nav--next').hidden = !many;
    }

    function trapTab(e) {
      var focusable = Array.prototype.slice.call(
        el.querySelectorAll('button:not([hidden]), a[href]:not([hidden]), video')
      ).filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function build() {
      if (el) return el;

      el = document.createElement('div');
      el.className = 'sh-lb';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Reel player');
      el.hidden = true;
      el.innerHTML =
        '<div class="sh-lb__backdrop" data-lb-close></div>' +
        '<button class="sh-lb__close" type="button" data-lb-close aria-label="Close player">' + X_SVG + '</button>' +
        '<div class="sh-lb__stage">' +
          '<div class="sh-lb__row">' +
            '<button class="sh-lb__nav sh-lb__nav--prev" type="button" data-lb-step="-1" aria-label="Previous reel">' + chevronSvg('prev') + '</button>' +
            '<div class="sh-lb__frame"></div>' +
            '<button class="sh-lb__nav sh-lb__nav--next" type="button" data-lb-step="1" aria-label="Next reel">' + chevronSvg('next') + '</button>' +
          '</div>' +
          '<div class="sh-lb__meta">' +
            '<p class="sh-lb__caption"></p>' +
            '<div class="sh-lb__actions">' +
              '<a class="sh-lb__insta" target="_blank" rel="noopener">' + IG_SVG + '<span>View on Instagram</span></a>' +
              '<a class="sh-lb__shop">Shop this</a>' +
            '</div>' +
          '</div>' +
        '</div>';

      document.body.appendChild(el);

      el.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        if (e.target.closest('[data-lb-close]')) { close(); return; }
        var stepper = e.target.closest('[data-lb-step]');
        if (stepper) step(parseInt(stepper.getAttribute('data-lb-step'), 10) || 0);
      });

      document.addEventListener('keydown', function (e) {
        if (!isOpen()) return;
        if (e.key === 'Escape') { close(); }
        else if (e.key === 'ArrowRight') { step(1); }
        else if (e.key === 'ArrowLeft') { step(-1); }
        else if (e.key === 'Tab') { trapTab(e); }
      });

      return el;
    }

    function open(list, i) {
      build();
      group = list;
      index = i;
      lastFocus = document.activeElement;

      reelsPauseAll();
      el.hidden = false;
      document.documentElement.classList.add('sh-lb-open');
      render();
      el.querySelector('.sh-lb__close').focus();
    }

    function step(by) {
      if (!by || group.length < 2) return;
      index = (index + by + group.length) % group.length;
      render();
    }

    function close() {
      if (!isOpen()) return;
      teardown();
      el.hidden = true;
      document.documentElement.classList.remove('sh-lb-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      lastFocus = null;
      reelsResumeVisible();
    }

    return { open: open, close: close, isOpen: isOpen };
  })();

  function initReels(rail) {
    // shopify:section:load re-runs init() across the whole page, not just the
    // section that reloaded — without this the surviving rails pick up a
    // second click handler and a second observer on every editor save.
    if (rail.getAttribute('data-sh-reels-ready') === '1') return;
    rail.setAttribute('data-sh-reels-ready', '1');

    var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-sh-reel]'));
    if (!cards.length) return;

    cards.forEach(function (card, i) {
      var opener = card.querySelector('.sh-reel__open');
      if (!opener) return;
      opener.addEventListener('click', function (e) {
        // Leave modified clicks alone — cmd/ctrl-click opening the product
        // page in a new tab is still a useful escape hatch.
        if (e.metaKey || e.ctrlKey || e.shiftKey || (e.button && e.button !== 0)) return;
        e.preventDefault();
        reelPlayer.open(cards, i);
      });
    });

    var videos = cards.map(function (card) { return card.querySelector('video'); })
                      .filter(Boolean);
    if (!videos.length) return;

    if (reduceMotion) {
      videos.forEach(function (v) {
        v.removeAttribute('autoplay');
        v.pause();
      });
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var rec = entry.target.shReelRecord;
        if (rec) rec.visible = entry.isIntersecting;

        if (entry.isIntersecting && !reelPlayer.isOpen()) {
          var p = entry.target.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          entry.target.pause();
        }
      });
    }, { threshold: 0.2 });

    videos.forEach(function (v) {
      var rec = { el: v, visible: false };
      v.shReelRecord = rec;
      reelVideos.push(rec);
      io.observe(v);
    });
  }

  /* ---------- reveal on scroll ---------- */

  function initReveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll('.sh-reveal'));
    if (!els.length) return;

    // Only hide things once we know we can reveal them again. Without
    // IntersectionObserver (or with reduced motion) the flag is never
    // set, so .sh-reveal stays fully visible via CSS.
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    document.documentElement.classList.add('js-reveal-ready');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    els.forEach(function (el) { io.observe(el); });
  }

  function init() {
    document.querySelectorAll('[data-sh-hero]').forEach(initHero);
    document.querySelectorAll('[data-sh-tabs]').forEach(initTabs);
    document.querySelectorAll('[data-sh-reels]').forEach(initReels);
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
